import { Injectable, signal } from '@angular/core';

import { ToasterStatus, type ToastContent } from '../components/toaster/toaster.types';

export const DEFAULT_TOASTER_DURATION_MS = 5000;

export const MAX_TOASTS = 5;

export interface Toast extends ToastContent {
    id: string;
    status: ToasterStatus;
    title: string;
    message?: string;
    duration: number;
    restartToken: number;
}

export type ToastInput = ToastContent & { id?: string; duration?: number };

const isTransient = (toast: Toast): boolean => toast.duration > 0;

@Injectable({ providedIn: 'root' })
export class ToasterStore {
    private readonly _toasts = signal<readonly Toast[]>([]);
    readonly toasts = this._toasts.asReadonly();

    add({ duration = DEFAULT_TOASTER_DURATION_MS, id: providedId, ...toast }: ToastInput): string | undefined {
        const duplicate = this._toasts().find((existing) =>
            providedId
                ? existing.id === providedId
                : existing.status === toast.status &&
                  existing.title === toast.title &&
                  existing.subtitle === toast.subtitle &&
                  existing.message === toast.message,
        );

        const persistentCount = this._toasts().filter((existing) => !isTransient(existing)).length;
        const noRoomForMorePersistent = persistentCount >= MAX_TOASTS - 1;

        if (duplicate) {
            const flipsToPersistent = duration <= 0 && isTransient(duplicate);
            const restartDuration = flipsToPersistent && noRoomForMorePersistent ? duplicate.duration : duration;
            this._toasts.update((toasts) =>
                toasts.map((existing) =>
                    existing.id === duplicate.id
                        ? {
                              ...toast,
                              id: existing.id,
                              duration: restartDuration,
                              restartToken: existing.restartToken + 1,
                          }
                        : existing,
                ),
            );
            return duplicate.id;
        }

        if (duration <= 0 && noRoomForMorePersistent) return undefined;

        const id = providedId ?? crypto.randomUUID();
        this._toasts.update((toasts) => this.withinCap([...toasts, { ...toast, duration, id, restartToken: 0 }]));
        return id;
    }

    private withinCap(toasts: readonly Toast[]): readonly Toast[] {
        let excess = toasts.length - MAX_TOASTS;
        if (excess <= 0) return toasts;

        return toasts.filter((toast) => {
            if (excess > 0 && isTransient(toast)) {
                excess--;
                return false;
            }
            return true;
        });
    }

    dismiss(id: string): void {
        this._toasts.update((toasts) => toasts.filter((toast) => toast.id !== id));
    }

    clear(): void {
        this._toasts.set([]);
    }
}
